import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import axiosInstance from "../../../api/axiosInstance";
import Swal from "sweetalert2";
import { getTeethNos } from '../../../utils/functions';
const DentalServiceForm = forwardRef((props, ref) => {
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [showServiceSuggestions, setShowServiceSuggestions] = useState(false);
    const [hasActiveInsuranceCard, setHasActiveInsuranceCard] = useState(false);
    const [availableServices, setAvailableServices] = useState([]);
    const [serviceSearchQuery, setServiceSearchQuery] = useState("");
    const [filteredServices, setFilteredServices] = useState([]);
    const [companyPriceMap, setCompanyPriceMap] = useState({});

    const [serviceFormData, setServiceFormData] = useState({
        serviceId: "",
        extra_notes: "",
        discountType: "Amount",
        discountValue: 0,
        status: "Plan",
        details: []
    });
    const [customCost, setCustomCost] = useState(null);

    const loadAvailableServices = (mode) => {
        axiosInstance.get(
            `getDentalServices/${props.patientId}?dental_type=${mode}`
        ).then((response => {
            const list = (response.data?.data || []).map((item) => ({
                id: item.id,
                code: item.code,
                serviceName: item.name,
                description: item.description,
                standardCost: item.standard_price,
                secondaryCost: item.secondary_price,
                insurancePrice: item.insurance_price,
                duration: item.duration,
                DentalMode: item.DentalMode,
                isDental: item.isDental,
                vatLabel: item.vat,
                vatEnabled: (item.vat || "").toString().toLowerCase() === "yes",
                vatValue: Number(item.vat_value) || 0,
            }));
            setAvailableServices(list);
        })).catch(e => {
            console.error(e)
            setAvailableServices([]);
        });
    };

    useImperativeHandle(ref, () => ({
        toggleModal() {
            setIsServiceModalOpen(!isServiceModalOpen);
            checkPatientInsuranceCard();
        },
        setTheServiceFormData(d) {
            let data = JSON.parse(JSON.stringify(d))
            setServiceFormData(data)
            setCustomCost(null)
            const mode = d.dental_type ? d.dental_type : props.teethChartMode
            loadAvailableServices(mode)
        },
        setTheServiceSearchQuery(d) {
            setServiceSearchQuery(d)
        }
    }));


    useEffect(() => {
        if (serviceFormData.serviceId) {
            const selectedServiceData = availableServices.find(
                (s) => s.id + "" === serviceFormData.serviceId + ""
            );
            if (selectedServiceData) {
                setServiceSearchQuery(`${selectedServiceData?.code} - ${selectedServiceData?.serviceName}`)
            } else {
                setServiceSearchQuery(null)
            }
        }
    }, [availableServices, serviceFormData])

    useEffect(() => {
        if (serviceSearchQuery) {
            setFilteredServices(availableServices?.filter(
                (service) =>
                    service?.serviceName
                        ?.toLowerCase()
                        .includes(serviceSearchQuery?.toLowerCase()) ||
                    service.code?.toLowerCase().includes(serviceSearchQuery?.toLowerCase())))
        } else {
            setFilteredServices(availableServices)
        }
    }, [serviceSearchQuery, availableServices])

    const checkPatientInsuranceCard = async () => {
        try {
            const response = await axiosInstance.get(
                `getInsuranceCardsByPatientId/${props.patientId}`
            );
            const cards = Array.isArray(response.data?.data)
                ? response.data.data
                : [];
            const activeCard = cards.find(
                (card) =>
                    card.status === "Active" ||
                    (card.expiry_date && new Date(card.expiry_date) > new Date())
            );
            setHasActiveInsuranceCard(!!activeCard);
            if (activeCard?.company_id) {
                try {
                    const priceRes = await axiosInstance.get(`getCompanyServicePrices/${activeCard.company_id}`);
                    const rows = priceRes.data?.data || [];
                    const map = {};
                    rows.forEach(r => { if (r.company_price != null) map[r.service_id] = r.company_price; });
                    setCompanyPriceMap(map);
                } catch (e) {
                    setCompanyPriceMap({});
                }
            } else {
                setCompanyPriceMap({});
            }
        } catch (error) {
            console.error("Error checking insurance card:", error);
            setHasActiveInsuranceCard(false);
            setCompanyPriceMap({});
        }
    };

    const submitServiceForm = async (e) => {
        e.preventDefault();

        const resolvedPatientId = Number(props.patientId);
        const resolvedServiceId = Number(serviceFormData.serviceId) || null;

        if (!resolvedPatientId) {
            Swal.fire("Error", "Missing patient or service selection", "error");
            return;
        }

        const selectedServiceData = availableServices.find(
            (s) => Number(s.id) === resolvedServiceId
        );
        if (!selectedServiceData) {
            Swal.fire("Error", "Please select a valid service", "error");
            return;
        }
        await checkPatientInsuranceCard();
        const baseCost = customCost !== null
            ? customCost
            : hasActiveInsuranceCard
                ? companyPriceMap[selectedServiceData.id] ??
                selectedServiceData.insurancePrice ??
                selectedServiceData.insurance_price ??
                selectedServiceData.standardCost
                : selectedServiceData.standardCost;

        const discount =
            serviceFormData.discountType === "Percentage"
                ? (baseCost * serviceFormData.discountValue) / 100
                : serviceFormData.discountValue;
        const quantity = selectedServiceData?.DentalMode === "multiple"
            ? 1
            : (props.selectedTeeth.length || 1);
        const serviceData = {
            details: props.selectedTeeth.map(item => {
                return {
                    type: item.type,
                    id: item.id,
                    symbol: item.symbol
                }
            }),
            serviceId: resolvedServiceId,
            discount_type: serviceFormData.discountType,
            discount_value: discount,
            extra_notes: serviceFormData.extra_notes,
            quantity,
            amount: (() => {
                const effectiveCost = customCost !== null
                    ? customCost
                    : hasActiveInsuranceCard
                        ? companyPriceMap[selectedServiceData?.id] ??
                        selectedServiceData?.insurancePrice ??
                        selectedServiceData?.insurance_price ??
                        selectedServiceData?.standardCost
                        : selectedServiceData?.standardCost;
                return selectedServiceData?.DentalMode === "multiple"
                    ? effectiveCost
                    : effectiveCost * props.selectedTeeth.length;
            })(),
        };
        try {
            if (serviceFormData.id > 0) {
                await axiosInstance.put(
                    `UpdatePatientServices/${serviceFormData.id}`,
                    serviceData
                );
                Swal.fire("Success", "Updated successfully", "success");
            } else {
                await axiosInstance.post(`AddDentalPatientService`, {
                    patientId: resolvedPatientId,
                    ...serviceData,
                });
                // Deduct linked inventory items and log to stock movements
                if (resolvedServiceId) {
                    try {
                        await axiosInstance.post(`deductServiceInventory`, { service_id: resolvedServiceId, patient_id: resolvedPatientId });
                    } catch (e) {
                        console.warn("Inventory deduction failed:", e);
                    }
                }
                Swal.fire("Success", "Added Successfully", "success");
            }
            setIsServiceModalOpen(false);

            if (props.onAddedSuccessfully)
                props.onAddedSuccessfully()
        } catch (error) {
            console.error("Submit failed", error);
            const msg = error.response?.data?.message || "Something went wrong!";
            Swal.fire("Error", msg, "error");
        }
    };


    const fld = { fontSize: 12, height: 32, borderRadius: 7, border: '1px solid #dde3eb', padding: '0 10px', width: '100%', background: '#fff', color: '#2d3748', outline: 'none' };
    const lbl = { fontSize: 10, fontWeight: 700, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 3 };
    const col = (flex) => ({ flex, minWidth: 0 });

    return (<>
        {isServiceModalOpen && (() => {
            const s = availableServices.find(x => x.id === serviceFormData.serviceId);
            const effectiveCost = customCost !== null
                ? customCost
                : hasActiveInsuranceCard
                    ? companyPriceMap[s?.id] ?? s?.insurancePrice ?? s?.insurance_price ?? ""
                    : s?.standardCost ?? "";
            const totalCost = effectiveCost === "" ? "" : s?.DentalMode === "multiple"
                ? effectiveCost
                : effectiveCost * props.selectedTeeth.length;

            return (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 920, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px', borderBottom: '1px solid #e8ecf0' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#4680ff18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="ti ti-tooth" style={{ color: '#4680ff', fontSize: 17 }} />
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>
                                {serviceFormData.id > 0 ? 'Edit Service' : 'Add Service'}
                            </div>
                            <button type="button" onClick={() => setIsServiceModalOpen(false)}
                                style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5B6B79', fontSize: 14 }}>
                                <i className="ti ti-x" />
                            </button>
                        </div>

                        <form onSubmit={submitServiceForm}>
                            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                                {/* Row 1: Search */}
                                <div style={{ position: 'relative' }}>
                                    <label style={lbl}>Search & Select Service <span style={{ color: '#e53e3e' }}>*</span></label>
                                    <input
                                        type="text"
                                        style={{ ...fld, height: 34 }}
                                        placeholder="Type to search by name or code..."
                                        defaultValue={serviceSearchQuery}
                                        onChange={e => { setServiceSearchQuery(e.target.value); setShowServiceSuggestions(true); }}
                                        onFocus={() => setShowServiceSuggestions(true)}
                                    />
                                    {showServiceSuggestions && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1100, background: '#fff', border: '1px solid #e8ecf0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
                                            {filteredServices.length === 0
                                                ? <div style={{ padding: '10px 14px', fontSize: 12, color: '#9aa0ac' }}>No matches</div>
                                                : filteredServices.map(service => (
                                                    <div key={service.id}
                                                        onClick={() => {
                                                            setServiceFormData({ ...serviceFormData, serviceId: service.id });
                                                            setServiceSearchQuery(`${service.code} - ${service.serviceName}`);
                                                            setCustomCost(null);
                                                            setShowServiceSuggestions(false);
                                                        }}
                                                        style={{
                                                            padding: '8px 14px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f0f2f5',
                                                            background: service.id === serviceFormData.serviceId ? '#4680ff0d' : '#fff',
                                                            color: service.id === serviceFormData.serviceId ? '#1A3A70' : '#2d3748',
                                                            fontWeight: service.id === serviceFormData.serviceId ? 600 : 400,
                                                        }}
                                                        onMouseEnter={e => { if (service.id !== serviceFormData.serviceId) e.currentTarget.style.background = '#f8f9fb'; }}
                                                        onMouseLeave={e => { if (service.id !== serviceFormData.serviceId) e.currentTarget.style.background = '#fff'; }}
                                                    >
                                                        <span style={{ fontWeight: 600 }}>{service.code}</span> — {service.serviceName}
                                                        <span style={{ marginLeft: 8, fontSize: 11, color: '#5B6B79' }}>
                                                            {hasActiveInsuranceCard
                                                                ? `BHD ${companyPriceMap[service.id] ?? service.insurancePrice ?? service.insurance_price ?? service.standardCost}`
                                                                : `BHD ${service.standardCost}`}
                                                        </span>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>

                                {/* Row 2: Code | Name | Teeth | Cost | VAT | Total */}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <div style={col('0 0 100px')}>
                                        <label style={lbl}>Code</label>
                                        <input style={{ ...fld, background: '#f8f9fb' }} value={s?.code || ''} readOnly />
                                    </div>
                                    <div style={col(1)}>
                                        <label style={lbl}>Service Name</label>
                                        <input style={{ ...fld, background: '#f8f9fb' }} value={s?.serviceName || ''} readOnly />
                                    </div>
                                    <div style={col('0 0 150px')}>
                                        <label style={lbl}>Selected Teeth</label>
                                        <input style={{ ...fld, background: '#f8f9fb' }} value={getTeethNos(serviceFormData?.details?.length > 0 ? serviceFormData.details : props.selectedTeeth, props.isFdi).join(', ')} readOnly />
                                    </div>
                                    <div style={col('0 0 130px')}>
                                        <label style={lbl}>{hasActiveInsuranceCard ? 'Insurance Cost' : 'Standard Cost'}</label>
                                        <input type="number" style={fld}
                                            value={customCost !== null ? customCost : hasActiveInsuranceCard ? companyPriceMap[s?.id] ?? s?.insurancePrice ?? s?.insurance_price ?? '' : s?.standardCost ?? ''}
                                            onChange={e => { const v = e.target.value; setCustomCost(v === '' ? null : parseFloat(v) || 0); }}
                                            min="0" step="0.01" />
                                    </div>
                                    <div style={col('0 0 70px')}>
                                        <label style={lbl}>VAT</label>
                                        <input style={{ ...fld, background: '#f8f9fb' }} value={s ? (s.vatEnabled ? `${s.vatValue}%` : '0%') : ''} readOnly />
                                    </div>
                                    <div style={col('0 0 120px')}>
                                        <label style={lbl}>Total Cost</label>
                                        <input style={{ ...fld, background: '#f0fdf4', fontWeight: 700, color: '#2CA87F' }} value={totalCost} placeholder="teeth × price" readOnly />
                                    </div>
                                </div>

                                {/* Row 3: Discount Type | Discount Value */}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <div style={col('0 0 220px')}>
                                        <label style={lbl}>Discount Type</label>
                                        <select style={{ ...fld, cursor: 'pointer' }} value={serviceFormData.discountType}
                                            onChange={e => setServiceFormData({ ...serviceFormData, discountType: e.target.value })}>
                                            <option value="Amount">By Amount BHD</option>
                                            <option value="Percentage">By Percentage (%)</option>
                                        </select>
                                    </div>
                                    <div style={col('0 0 150px')}>
                                        <label style={lbl}>Discount Value</label>
                                        <input type="number" style={fld} value={serviceFormData.discountValue}
                                            onChange={e => setServiceFormData({ ...serviceFormData, discountValue: parseFloat(e.target.value) || 0 })}
                                            min="0" step="0.01" />
                                    </div>
                                    <div style={col(1)} />
                                </div>

                                {/* Row 4: Description | Extra Notes */}
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <div style={col(1)}>
                                        <label style={lbl}>Description</label>
                                        <textarea style={{ ...fld, height: 56, resize: 'none', padding: '6px 10px' }} value={s?.description || ''} readOnly />
                                    </div>
                                    <div style={col(1)}>
                                        <label style={lbl}>Extra Notes</label>
                                        <textarea style={{ ...fld, height: 56, resize: 'none', padding: '6px 10px' }}
                                            value={serviceFormData.extra_notes}
                                            onChange={e => setServiceFormData({ ...serviceFormData, extra_notes: e.target.value })} />
                                    </div>
                                </div>

                            </div>

                            {/* Footer */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #f0f2f5' }}>
                                <button type="button" onClick={() => setIsServiceModalOpen(false)}
                                    style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: '#5B6B79', cursor: 'pointer' }}>
                                    Cancel
                                </button>
                                <button type="submit"
                                    style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#4680ff', color: '#fff', fontWeight: 600 }}>
                                    {serviceFormData.id > 0 ? 'Update' : 'Save'} Service
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            );
        })()}
    </>)
});
export default DentalServiceForm;